import {
    TabStrip,
    TabStripSelectEventArguments,
    TabStripTab,
} from '@progress/kendo-react-layout'
import GenericError from 'components/GenericError/GenericError'
import Translation from 'components/Translation'
import { DEFAULT_ERROR_STATE, IError } from 'context/AppContext'
import { AuthContext, useAuth } from 'context/AuthContext'
import { useTenantContext } from 'context/TenantContext'
import React, { SetStateAction, useCallback, useState } from 'react'
import {
    useForm,
    UseFormGetValues,
    UseFormHandleSubmit,
    UseFormRegister,
    UseFormReset,
    UseFormSetError,
} from 'react-hook-form'
import { classNames } from 'utils/dom-helpers'
import { postApiRequest, putApiRequest } from 'utils/fetch-helper'
import { toYmd } from 'utils/string-localization-helper'
import { IEditor } from '../CustomBookingPage/Editor'
import ErrorModal from '../ErrorModal'
import { createModal } from '../ModalFactory'
import { ModalWrapper } from '../ModalWrapper'
import BasicInformation from './BasicInformation'
import RedemptionInfo from './RedemptionInfo'

const ModalProvider = createModal(
    AuthContext,
    'CreateCouponModal',
    () => (
        <>
            <i className="feather feather-plus mr-4" />
            <span className="circular">Create</span>
        </>
    ),
    () => (
        <span className="inline-block w-6 h-6 text-primary text-center font-extralight">
            <i className="feather feather-chevron-left " />
        </span>
    )
)

export const CreateCoupontModalOpener = ModalProvider.Opener
export const CreateCouponModalCloser = ModalProvider.Closer

export interface ICouponModal {
    register: UseFormRegister<any>
    handleSubmit: UseFormHandleSubmit<any>
    formState: {
        errors: any
    }
    setError: UseFormSetError<any>
    reset: UseFormReset<any>
    getValues: UseFormGetValues<any>
    selected: number
    selectedOption: number
    setSelectedOption: React.Dispatch<SetStateAction<number>>
    isDirty: boolean
    setIsDirty: React.Dispatch<SetStateAction<boolean>>
}
const mandatoryFields = [
    'categoryIds',
    'code',
    'discountType',
    'discountValue',
    'effectiveDate',
    'name',
    'serviceIds',
    'tenantId',
    'usageLimit',
    'usagePerClient',
]

function CreateCouponModal() {
    const { CreateCouponModal } = useAuth()
    const { setRefetchCoupons, tenant } = useTenantContext()

    const { attributes, setAttributes } = CreateCouponModal
    const [selected, setSelected] = useState(0)
    const [errorMessage, setErrorMessage] =
        useState<IError>(DEFAULT_ERROR_STATE)
    const [successMessage, setSuccessMessage] = useState('')
    const [selectedOption, setSelectedOption] = useState(1)
    const [isDirty, setIsDirty] = useState(false)

    const handleSelect = (e: TabStripSelectEventArguments) => {
        setSelected(e.selected)
    }

    const {
        register,
        handleSubmit,
        formState: { errors },
        setError,
        reset,
        getValues,
    } = useForm<any>({
        mode: 'onSubmit',
    })

    const tabProps = {
        register,
        handleSubmit,
        formState: { errors },
        setError,
        reset,
        getValues,
        selected,
        selectedOption,
        setSelectedOption,
        isDirty,
        setIsDirty,
    }
    const isEdit = attributes?.id
    const handleClose = () => {
        setIsDirty(false)
        setAttributes({})
        setSelected(0)
        reset()
        CreateCouponModal.close()
    }

    const transformRequest = () => {
        const payload: any = {
            categoryIds: attributes?.selected_categories,
            code: attributes?.code,
            discountType: attributes?.discountType || 'PERCENT',
            discountValue: attributes?.discountValue,
            effectiveDate: attributes?.effectiveDate || toYmd(new Date()),
            expiryDate: attributes?.expiryDate,
            id: attributes?.id || '',
            locationIds: attributes?.locationIds,
            name: attributes?.name,
            serviceIds: attributes?.selected_services,
            tenantId: tenant?.id,
            usageLimit: attributes?.usageLimit || 1000000,
            usagePerClient: attributes?.usagePerClient || 1,
            description: attributes?.description,
        }
        if (attributes?.redemptionType === 'MEMBERSHIP') {
            delete payload?.serviceIds
            payload['membershipIds'] = attributes?.selected_services
        }
        return payload
    }

    const editHandler = async (payload: any) => {
        try {
            const res = await putApiRequest(
                `/v1/coupons/${payload?.id}`,
                payload
            )

            if (!res.ok) {
                const error: IError = {
                    message: `Error in updating coupon: ${res?.message}`,
                    tag: res?.tag,
                }
                throw error
            } else {
                triggerApiSuccess('Successfully updated a coupon')
            }
        } catch (err) {
            triggerApiFail(err)
        }
    }

    const addHandler = async (payload: any) => {
        try {
            const res = await postApiRequest(`/v1/coupons`, payload)
            const error: IError = {
                message: `Error in adding coupon: ${res?.message}`,
                tag: res?.tag,
            }
            if (!res.ok) throw error
            else {
                triggerApiSuccess('Successfully added a coupon')
            }
        } catch (err) {
            triggerApiFail(err)
        }
    }

    const triggerApiSuccess = (message: string) => {
        setRefetchCoupons(true)
        setSuccessMessage(message)
    }
    const triggerApiFail = (error: IError) => {
        setErrorMessage(error)
    }

    const onSubmit = (data: any) => {
        const payload = transformRequest()
        switch (attributes?.operationType) {
            case 'edit':
                editHandler(payload)
                break
            case 'add':
                addHandler(payload)
                break
            default:
                ;() => {}
        }
    }

    const disableNext =
        !attributes?.description ||
        !attributes?.code ||
        !attributes?.discountType ||
        !attributes?.discountValue

    const disableSave =
        //@ts-ignore
        ((!attributes?.selected_services?.length < 1) as boolean) ||
        //@ts-ignore
        ((!attributes?.selected_categories?.length < 1) as boolean)

    const getModalFooter = useCallback(() => {
        return (
            <div className="p-5 pt-0 flex justify-end gap-3 ">
                <div>
                    <button
                        type="button"
                        className="btn-gap border-gray-200 rounded-lg border text-sm"
                        onClick={() => handleClose()}
                    >
                        Cancel
                    </button>
                </div>
                <div>
                    {selected == 1 && (
                        <>
                            <button
                                type="button"
                                className="btn-gap text-sm border-gray-200 rounded-lg border "
                                onClick={() => {
                                    if (selected == 1)
                                        setSelected((prev) => prev - 1)
                                }}
                            >
                                Previous
                            </button>{' '}
                            <button
                                type="submit"
                                className={classNames(
                                    'p-2 text-sm rounded-lg   text-white',
                                    !disableSave || !isDirty
                                        ? 'bg-gray-400 cursor-not-allowed text-white'
                                        : 'bg-sky-800 border-sky-800'
                                )}
                                disabled={!disableSave || !isDirty}
                            >
                                Save
                            </button>
                        </>
                    )}
                    {selected == 0 && (
                        <button
                            type="button"
                            className={classNames(
                                'btn-gap text-sm border-gray-200 rounded-lg border',
                                disableNext
                                    ? 'bg-gray-400 cursor-not-allowed text-white'
                                    : ''
                            )}
                            onClick={() => {
                                if (selected < 1)
                                    setSelected((prev) => prev + 1)
                            }}
                            disabled={disableNext}
                        >
                            Next
                        </button>
                    )}
                </div>
            </div>
        )
    }, [selected, disableNext, disableSave, isDirty])

    return (
        <ModalProvider.Visible>
            <ModalWrapper>
                <div className="bg-white shadow-2xl sm:rounded-md relative border-2 border-gray-100 my-auto">
                    <div className="flex px-4 z-10 w-full pt-4 bg-opacity-30 pb-3 bg-primary-lighter">
                        <div>
                            <ul className="ml-4 text-sm flex gap-1 breadcrumbs">
                                <Translation
                                    render_as="li"
                                    content_key="dashboard"
                                />
                                <Translation
                                    render_as="li"
                                    content_key="coupons"
                                />
                            </ul>
                            <span className="text-lg text-primary-dark">
                                <span
                                    onClick={() => {
                                        handleClose()
                                    }}
                                >
                                    <CreateCouponModalCloser className="self-center pt-1" />
                                </span>
                                <span className="circular ml-3">
                                    {isEdit ? (
                                        `${attributes?.code}`
                                    ) : (
                                        <Translation content_key="new_coupon" />
                                    )}
                                </span>
                            </span>
                        </div>
                    </div>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <TabStrip
                            selected={selected}
                            onSelect={handleSelect}
                            className="z-20"
                            animation={false}
                        >
                            <TabStripTab
                                title={
                                    <Translation content_key="basic_info_coupon" />
                                }
                            >
                                <div className="p-5 w-full">
                                    <BasicInformation {...tabProps} />
                                </div>
                            </TabStripTab>
                            {!disableNext && (
                                <TabStripTab
                                    title={
                                        <Translation content_key="select_redemption_type" />
                                    }
                                >
                                    <div className="p-5 w-full">
                                        <RedemptionInfo {...tabProps} />
                                    </div>
                                </TabStripTab>
                            )}
                        </TabStrip>
                        {getModalFooter()}
                    </form>
                    {!!errorMessage?.message.length && (
                        <GenericError
                            error_message={errorMessage?.message}
                            onCloseModal={() => {
                                setErrorMessage(DEFAULT_ERROR_STATE)
                                setSelected(0)
                            }}
                            tag={errorMessage?.tag}
                        />
                    )}
                    {!!successMessage.length && (
                        <ErrorModal
                            error_message={successMessage}
                            type="success"
                            onCloseModal={() => {
                                setSuccessMessage('')
                                setSelected(0)
                                CreateCouponModal.close()
                            }}
                        />
                    )}
                </div>
            </ModalWrapper>
        </ModalProvider.Visible>
    )
}

export default CreateCouponModal
